import classnames from 'classnames';
import Markdown from 'markdown-to-jsx';
import * as React from 'react';

import { isReadOnlyMode } from '../../globals';
import {
	ChatMessageStatusTypes,
	ChatMessageTypes,
	ChatMessageTypesClassMap,
	SenderTypes,
	SenderTypesClassMap,
	SimpleChatMessage,
} from '../../store';
import ErrorFailedToSend from '../ErrorFailedToSend';
import RatingDetailed from '../RatingDetailed';

interface SimpleMessageProps extends React.HTMLProps<HTMLDivElement> {
	sender: SenderTypes;
	messageData: SimpleChatMessage;
}

export const dataTestId = `${
	ChatMessageTypesClassMap[ChatMessageTypes.Simple]
}-message`;

export const SimpleMessage: React.FC<SimpleMessageProps> = ({
	sender,
	messageData,
	children,
}) => {
	const { id, status, requestFeedback, feedback } = messageData;
	const dataTestIdWithSender = `${dataTestId}-${SenderTypesClassMap[sender]}`;
	const [shouldShakeRating, setShouldShakeRating] = React.useState(false);

	React.useEffect(() => {
		const shakeTimeoutRef = window.setTimeout(() => {
			setShouldShakeRating(true);
		}, 1000);
		return () => {
			clearTimeout(shakeTimeoutRef);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (sender === SenderTypes.User) {
		return (
			<div
				id={dataTestIdWithSender}
				data-testid={dataTestIdWithSender}
				className="flex flex-col items-end mx-2 mb-2"
			>
				<div className="max-w-85pc">
					<div
						className={classnames(
							'whitespace-pre-line break-words px-3 py-2 self-end bg-lightgreen text-body-sm text-secondary rounded-md rounded-tr-none',
							{
								'bg-lightgreen': status !== ChatMessageStatusTypes.Failed,
								'border border-solid border-error bg-red':
									status === ChatMessageStatusTypes.Failed,
							}
						)}
					>
						{children}
					</div>
				</div>

				{id && status === ChatMessageStatusTypes.Failed && (
					<ErrorFailedToSend messageId={id} />
				)}
			</div>
		);
	}

	return (
		<div
			id={dataTestIdWithSender}
			data-testid={dataTestIdWithSender}
			className="flex flex-col items-start mx-2 mb-2"
		>
			<div className="max-w-85pc">
				<div className="border-gray-40 text-body-sm self-start bg-white border border-solid rounded-md rounded-tl-none">
					<div className="px-3 py-2 break-words whitespace-pre-line">
						{typeof children === 'string' ? (
							<Markdown>{children}</Markdown>
						) : (
							<React.Fragment>{children}</React.Fragment>
						)}
					</div>
					{requestFeedback && feedback && (
						<RatingDetailed
							className="border-gray-40 py-1 pl-3 pr-2 border-t border-solid"
							style={{ minWidth: '243px' }} // As narrow as the simple message can get on mobile devices (320px screen size)
							feedback={feedback}
							shakeLabel={shouldShakeRating}
							isReadOnly={isReadOnlyMode}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default SimpleMessage;
